import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, animate, useInView } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { ContactFormPopup } from '../ContactFormPopup';
function AnimatedCounter({
  value,
  suffix,
  inView
}: {
  value: number;
  suffix: string;
  inView: boolean;
}) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, latest => {
    if (value % 1 !== 0) return latest.toFixed(1);
    return Math.round(latest).toString();
  });
  useEffect(() => {
    if (inView) {
      const controls = animate(count, value, {
        duration: 2.2,
        ease: [0.16, 1, 0.3, 1]
      });
      return controls.stop;
    }
  }, [inView, value]);
  return <span className="flex items-baseline" data-id="element-202">
      <motion.span data-id="element-203">{rounded}</motion.span>
      <span data-id="element-204">{suffix}</span>
    </span>;
}
export function DDHero() {
  const {
    t
  } = useLanguage();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const statsRef = useRef(null);
  const isStatsInView = useInView(statsRef, {
    once: true,
    margin: '-30px'
  });
  const parseStat = (val: string) => {
    const match = val.match(/([\d.]+)(.*)/);
    if (match) return {
      value: parseFloat(match[1]),
      suffix: match[2]
    };
    return {
      value: 0,
      suffix: val
    };
  };
  const stats = [{
    ...parseStat(t('hero.stat1.value')),
    label: t('hero.stat1.label')
  }, {
    ...parseStat(t('hero.stat2.value')),
    label: t('hero.stat2.label')
  }, {
    ...parseStat(t('hero.stat3.value')),
    label: t('hero.stat3.label')
  }];
  return <>
      <section className="relative min-h-screen w-full overflow-hidden px-4 pt-24 pb-8 md:pb-0 md:pt-24 flex flex-col justify-center" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-205">
        {/* Subtle background accent */}
        <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none" data-id="element-206">
          <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl" style={{
          background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
        }} data-id="element-207" />
        </div>

        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col md:flex-row items-center justify-center gap-8 md:gap-12 lg:gap-16 flex-1" data-id="element-208">
          {/* Left side - Content */}
          <div className="w-full md:flex-1 text-center md:text-left" data-id="element-209">
            <motion.h1 initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} className="mb-6 text-4xl md:text-5xl lg:text-6xl" style={{
            color: 'var(--text-primary)'
          }} data-id="element-210">
              {t('dd.hero.title')}
              <br data-id="element-211" />
              <span style={{
              color: 'var(--accent)'
            }} data-id="element-212">
                {t('dd.hero.titleHighlight')}
              </span>
            </motion.h1>

            {/* Video - Mobile only */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.92
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} className="block md:hidden mb-8 rounded-xl overflow-hidden shadow-2xl border border-[var(--border)] aspect-video" style={{
            backgroundColor: 'var(--bg-tertiary)'
          }} data-id="element-213">
              <video src="https://cdn.magicpatterns.com/uploads/xptTdNfudsUtv2NDJFunbb/original-2de319f4767c5a0d4acf4f107b938504.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-214" />
            </motion.div>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.25,
            ease: [0.22, 1, 0.36, 1]
          }} className="mb-10 max-w-lg mx-auto md:mx-0 text-base md:text-lg" style={{
            color: 'var(--text-secondary)',
            lineHeight: '1.7'
          }} data-id="element-215">
              {t('dd.hero.subtitle')}
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.35,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start" data-id="element-216">
              <motion.a href="#services" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="group flex items-center justify-center gap-2 rounded-lg px-8 py-4 font-medium transition-all border" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)'
            }} data-id="element-217">
                {t('dd.services.title')} {t('dd.services.titleHighlight')}
              </motion.a>
              <motion.button onClick={() => setIsPopupOpen(true)} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="group flex items-center justify-center gap-2 rounded-lg px-8 py-4 font-medium transition-all" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-218">
                {t('dd.hero.cta')}
                <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-219" />
              </motion.button>
            </motion.div>
          </div>

          {/* Right side - Video (Desktop only) */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.92
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} className="hidden md:flex md:flex-1 items-center justify-center h-full" data-id="element-220">
            <div className="relative w-full max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-2xl border border-[var(--border)] aspect-video" style={{
            backgroundColor: 'var(--bg-tertiary)'
          }} data-id="element-221">
              <video src="https://cdn.magicpatterns.com/uploads/xptTdNfudsUtv2NDJFunbb/original-2de319f4767c5a0d4acf4f107b938504.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-222" />
            </div>
          </motion.div>
        </div>

        {/* Stats — same size as main page */}
        <div ref={statsRef} className="relative z-10 mx-auto max-w-5xl w-full px-4 pt-10 pb-8 md:pt-4 md:pb-6" data-id="element-223">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8" data-id="element-224">
            {stats.map((stat, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={isStatsInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.5 + index * 0.12,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex flex-col items-center text-center" data-id="element-225">
                <div className="text-3xl sm:text-3xl md:text-4xl font-semibold mb-1.5" style={{
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
              lineHeight: '1.1'
            }} data-id="element-226">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} inView={isStatsInView} data-id="element-227" />
                </div>
                <div className="text-xs sm:text-xs uppercase tracking-wider font-medium" style={{
              color: 'var(--text-muted)'
            }} data-id="element-228">
                  {stat.label}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-229" />
    </>;
}